import { Component, OnInit, OnDestroy, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { ProgramaService } from '../../../services/programa/programa.service';
import { Programa } from '../../../domain/programa.domain';

@Component({
  selector: 'app-programa-edit',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    TextareaModule,
    ToggleSwitchModule,
    ButtonModule
  ],
  templateUrl: './programa-edit.component.html',
  styleUrls: ['./programa-edit.component.scss']
})
export class ProgramaEditComponent implements OnInit, OnDestroy {
  programaForm!: FormGroup;
  loading = signal<boolean>(false);
  isEditMode = signal<boolean>(false);
  programaId: string | null = null;
  programa = signal<Programa | null>(null);

  private destroy$ = new Subject<void>();

  private programaService = inject(ProgramaService);

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.setupRouteParamListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupRouteParamListener(): void {
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const newId = params.get('id');
        if (newId !== this.programaId) {
          this.loadPrograma();
        }
      });
  }

  initForm(): void {
    this.programaForm = this.fb.group({
      codigo: ['', Validators.maxLength(255)],
      nombre: ['', [Validators.required, Validators.maxLength(255)]],
      conductores: [''],
      descripcion: ['', [Validators.required, Validators.maxLength(255)]],
      activo: [true]
    });
  }

  loadPrograma(): void {
    const newProgramaId = this.route.snapshot.paramMap.get('id');

    if (newProgramaId !== this.programaId) {
      this.programaForm.reset({ activo: true });
      this.isEditMode.set(false);
      this.programa.set(null);
    }

    this.programaId = newProgramaId;

    if (this.programaId && this.programaId !== 'nuevo') {
      this.isEditMode.set(true);
      this.loading.set(true);

      this.programaService.getById(this.programaId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (programa: Programa) => {
            this.loading.set(false);
            this.programa.set(programa);
            this.procesarDatosPrograma(programa);
          },
          error: (error) => {
            this.loading.set(false);
            console.error('Error al cargar programa:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Error al cargar los datos del programa',
              life: 3000
            });
          }
        });
    } else {
      this.isEditMode.set(false);
    }
  }

  private procesarDatosPrograma(programa: Programa): void {
    // El backend devuelve 'active', pero lo mapeamos a 'activo' internamente
    const programaData: any = programa;
    const activoValue = programaData.active !== undefined ? programaData.active : (programa.activo ?? true);

    this.programaForm.patchValue({
      codigo: programa.codigo || '',
      nombre: programa.nombre || '',
      conductores: programa.conductores ? programa.conductores.join('\n') : '',
      descripcion: programa.descripcion || '',
      activo: activoValue
    });
  }

  onSubmit(): void {
    if (this.programaForm.invalid) {
      this.markFormGroupTouched(this.programaForm);
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulario Inválido',
        detail: 'Por favor complete todos los campos requeridos',
        life: 3000
      });
      return;
    }

    const formValue = this.programaForm.value;

    // Convertir conductores de string a array
    const conductoresArray = formValue.conductores
      ? formValue.conductores.split('\n').map((c: string) => c.trim()).filter((c: string) => c.length > 0)
      : [];

    const programa: any = {
      codigo: formValue.codigo || '',
      nombre: formValue.nombre,
      conductores: conductoresArray,
      descripcion: formValue.descripcion || '',
      active: formValue.activo ?? true  // Backend usa 'active' en lugar de 'activo'
    };

    this.loading.set(true);

    const operation = this.isEditMode()
      ? this.programaService.update(this.programaId!, programa)
      : this.programaService.create(programa);

    operation
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: this.isEditMode()
              ? 'Programa actualizado correctamente'
              : 'Programa creado correctamente',
            life: 3000
          });
          setTimeout(() => {
            this.router.navigate(['/programas']);
          }, 1500);
        },
        error: (error) => {
          this.loading.set(false);
          console.error('Error al guardar programa:', error);

          let errorMessage = 'Error al guardar el programa';
          if (error.error?.violations) {
            const violations = error.error.violations;
            errorMessage = violations.map((v: any) => v.message).join(', ');
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          } else if (error.status === 401) {
            errorMessage = 'Sesión expirada. Por favor, inicia sesión nuevamente.';
          } else if (error.status === 403) {
            errorMessage = 'No tienes permisos para realizar esta acción.';
          }

          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
            life: 5000
          });
        }
      });
  }

  onCancel(): void {
    this.router.navigate(['/programas']);
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  hasError(fieldName: string, errorType: string): boolean {
    const field = this.programaForm.get(fieldName);
    return !!(field?.hasError(errorType) && field?.touched);
  }

  getErrorMessage(fieldName: string): string {
    const field = this.programaForm.get(fieldName);
    if (field?.hasError('required')) {
      return 'Este campo es requerido';
    }
    if (field?.hasError('maxlength')) {
      const maxLength = field.errors?.['maxlength'].requiredLength;
      return `Máximo ${maxLength} caracteres`;
    }
    return '';
  }
}
